import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Eraser, MousePointer2, Pencil, RotateCcw, Save, SlidersHorizontal, Type } from 'lucide-react';
import ImageEditingCanvas from './canvas-modes/ImageEditingCanvas';
import ImageEditPromptBox from './ImageEditPromptBox';
import KeyframeEditingSidebar from './sidebar-modes/KeyframeEditingSidebar';
import { useAssetMentionSupport } from './useAssetMentionSupport';
import { enhanceImagePrompt } from '../../lib/api';
import { useEditorUIStore, useProjectKeyframesStore } from '../../stores';
import { getAssetMediaType } from '../../utils/assetImageResolver';
import './ImageEditingWorkspace.css';

const DEFAULT_ADJUSTMENTS = { brightness: 0, contrast: 0, saturation: 0, temperature: 0, tint: 0 };
const AUTO_EDIT_PROMPT = 'Recreate the image, blend in patched parts.';
const IMAGE_REFERENCE_MEDIA_TYPES = ['image'];
const SELECTION_EDIT_PREFIX = 'Edit the subject in the red rectangle: ';

const createEmptyMagicSelectState = () => ({
  isSearching: false,
  clickPosition: null,
  normalizedPosition: null,
  selections: [],
  mergedMaskOverlayUrl: null,
  mergedMaskedImageUrl: null,
  maskedImageUrl: null,
  maskOverlayUrl: null,
  bounds: null,
  imageWidth: null,
  imageHeight: null,
  selectedAction: null,
  editPrompt: '',
  duplicatedLayer: null,
  cutoutImageUrl: null,
});

const TOOL_OPTIONS = [
  { id: 'cursor', label: 'Select', icon: MousePointer2 },
  { id: 'pen', label: 'Pen', icon: Pencil },
  { id: 'eraser', label: 'Eraser', icon: Eraser },
  { id: 'text', label: 'Text', icon: Type },
  { id: 'adjustments', label: 'Adjust', icon: SlidersHorizontal },
];

function hasAdjustments(adjustments) {
  const { brightness = 0, contrast = 0, saturation = 0, temperature = 0, tint = 0 } = adjustments || {};
  return brightness !== 0 || contrast !== 0 || saturation !== 0 || temperature !== 0 || tint !== 0;
}

function loadImageElement(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (url && !url.startsWith('blob:') && !url.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function createSelectionGuideFile(sourceUrl, bounds, filename = 'selection-guide.png') {
  const img = await loadImageElement(sourceUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (!width || !height) throw new Error('Unable to read selected image dimensions');

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  const x = Math.max(0, Math.round(bounds.x || 0));
  const y = Math.max(0, Math.round(bounds.y || 0));
  const w = Math.max(1, Math.round(bounds.width || 1));
  const h = Math.max(1, Math.round(bounds.height || 1));
  const lineWidth = Math.max(6, Math.round(Math.min(w, h) * 0.035));
  ctx.save();
  ctx.strokeStyle = '#ff1f1f';
  ctx.lineWidth = lineWidth;
  ctx.setLineDash([Math.max(12, lineWidth * 2), Math.max(8, lineWidth)]);
  ctx.strokeRect(x, y, w, h);
  ctx.setLineDash([]);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(2, Math.round(lineWidth * 0.28));
  ctx.strokeRect(x, y, w, h);
  ctx.restore();

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to create selection guide image'));
        return;
      }
      resolve(new File([blob], filename, { type: 'image/png' }));
    }, 'image/png');
  });
}

function AdjustmentControl({ label, value, onChange }) {
  return (
    <label className="image-editing-adjustment">
      <span>{label}</span>
      <input
        type="range"
        min="-100"
        max="100"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <output>{value}</output>
    </label>
  );
}

function ImageEditingWorkspace({
  projectId,
  imageId,
  imageUrl,
  imageName = 'Image',
  allAssets = [],
  assetMentionScope = 'project',
  initialPendingUrl = null,
  onClearInitialPendingUrl = () => {},
  onGeneratePreview,
  onCommitPreview,
  onCommitUpload,
  onCommitted = () => {},
  onSaveComplete = () => {},
  onBack = null,
  variant = 'embedded',
  sidePanel = null,
  versions = [],
  activeVersionNumber = 0,
  onRestoreVersion = null,
  isRestoringVersion = false,
  onCameraAnglePreview = null,
  onMagicSelect = null,
  onStartCreatingAsset = () => {},
  onStartUploadingAsset = () => {},
  title = 'Edit Asset',
  promptPlaceholder = 'Describe changes to the image...',
}) {
  const editorRef = useRef(null);
  const magicSelectSelectionsRef = useRef([]);
  const prevTextLayersCountRef = useRef(0);
  const prevImageLayersCountRef = useRef(0);

  const imageModel = useEditorUIStore((state) => state.imageEditModel);
  const setImageModel = useEditorUIStore((state) => state.setImageEditModel);
  const previewingVersionNumber = useEditorUIStore((state) => state.previewingKeyframeVersionNumber);
  const projectKeyframes = useProjectKeyframesStore((state) => state.keyframes);

  const [activeTool, setActiveTool] = useState('cursor');
  const [penColor, setPenColor] = useState('#ff0000');
  const [textLayers, setTextLayers] = useState([]);
  const [imageLayers, setImageLayers] = useState([]);
  const [colorAdjustments, setColorAdjustments] = useState(DEFAULT_ADJUSTMENTS);
  const [prompt, setPrompt] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [contentPolicyError, setContentPolicyError] = useState(null);
  const [hasPendingPreview, setHasPendingPreview] = useState(Boolean(initialPendingUrl));
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [cameraAngle, setCameraAngle] = useState({ horizontal: 0, vertical: 0, zoom: 5 });
  const [isCameraAngleGenerating, setIsCameraAngleGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isEnhancingPrompt, setIsEnhancingPrompt] = useState(false);
  const [magicSelectState, setMagicSelectState] = useState(createEmptyMagicSelectState);

  useEffect(() => {
    magicSelectSelectionsRef.current = magicSelectState.selections || [];
  }, [magicSelectState.selections]);

  useEffect(() => {
    setHasPendingPreview(Boolean(initialPendingUrl));
  }, [initialPendingUrl]);

  const sortedVersions = useMemo(
    () => (Array.isArray(versions) ? [...versions].sort((a, b) => (a.versionNumber ?? 0) - (b.versionNumber ?? 0)) : []),
    [versions],
  );

  const previewingVersion = useMemo(() => {
    if (previewingVersionNumber == null || previewingVersionNumber === activeVersionNumber) return null;
    return sortedVersions.find((version) => version.versionNumber === previewingVersionNumber) || null;
  }, [activeVersionNumber, previewingVersionNumber, sortedVersions]);

  const displayedImageUrl = previewingVersion?.imageUrl || previewingVersion?.keyframeUrl || imageUrl;
  const isVersionPreview = Boolean(previewingVersion);

  useEffect(() => {
    const initialEntry = {
      id: `asset-initial-${imageId}-${Date.now()}`,
      label: 'Initial',
      timestamp: Date.now(),
      currentUrl: imageUrl,
      textLayers: [],
      imageLayers: [],
      colorAdjustments: DEFAULT_ADJUSTMENTS,
      canvasDataUrl: null,
      pendingUrl: null,
      pendingPrompt: '',
    };
    setHistoryEntries([initialEntry]);
    setHistoryIndex(0);
    setTextLayers([]);
    setImageLayers([]);
    setColorAdjustments(DEFAULT_ADJUSTMENTS);
    setPrompt('');
    setIsEnhancingPrompt(false);
    setMagicSelectState(createEmptyMagicSelectState());
    prevTextLayersCountRef.current = 0;
    prevImageLayersCountRef.current = 0;
  }, [imageId, imageUrl]);

  const ensureAutoEditPrompt = useCallback(() => {
    setPrompt((currentPrompt) => (
      currentPrompt && currentPrompt.trim() ? currentPrompt : AUTO_EDIT_PROMPT
    ));
  }, []);

  useEffect(() => {
    const previousCount = prevTextLayersCountRef.current;
    const currentCount = textLayers.length;
    if (previousCount === 0 && currentCount > 0) {
      ensureAutoEditPrompt();
    }
    prevTextLayersCountRef.current = currentCount;
  }, [ensureAutoEditPrompt, textLayers.length]);

  useEffect(() => {
    const previousCount = prevImageLayersCountRef.current;
    const currentCount = imageLayers.length;
    if (previousCount === 0 && currentCount > 0) {
      ensureAutoEditPrompt();
    }
    prevImageLayersCountRef.current = currentCount;
  }, [ensureAutoEditPrompt, imageLayers.length]);

  const resetTransientInputs = useCallback(() => {
    setPrompt('');
    setUploadedImages([]);
    setContentPolicyError(null);
  }, []);

  const pushHistory = useCallback((label, overrides = {}) => {
    const pendingPreview = editorRef.current?.getPendingPreview?.();
    const entry = {
      id: `asset-hist-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      label,
      timestamp: Date.now(),
      currentUrl: overrides.currentUrl || editorRef.current?.getCurrentUrl?.() || imageUrl,
      textLayers: overrides.textLayers || textLayers,
      imageLayers: overrides.imageLayers || imageLayers,
      colorAdjustments: overrides.colorAdjustments || colorAdjustments,
      canvasDataUrl: overrides.canvasDataUrl ?? editorRef.current?.getCanvasDataUrl?.() ?? null,
      pendingUrl: overrides.pendingUrl ?? pendingPreview?.url ?? null,
      pendingPrompt: overrides.pendingPrompt ?? pendingPreview?.prompt ?? '',
    };
    setHistoryEntries((prev) => {
      const next = prev.slice(0, historyIndex + 1);
      next.push(entry);
      setHistoryIndex(next.length - 1);
      return next;
    });
  }, [colorAdjustments, historyIndex, imageLayers, imageUrl, textLayers]);

  const restoreHistoryEntry = useCallback((entry) => {
    if (!entry) return;
    if (entry.currentUrl) editorRef.current?.updateCurrentUrl?.(entry.currentUrl, false);
    editorRef.current?.setPendingPreviewFromHistory?.(entry.pendingUrl || null, entry.pendingPrompt || '');
    editorRef.current?.restoreCanvasDataUrl?.(entry.canvasDataUrl || null);
    setTextLayers(entry.textLayers || []);
    setImageLayers(entry.imageLayers || []);
    setColorAdjustments(entry.colorAdjustments || DEFAULT_ADJUSTMENTS);
    setHasPendingPreview(Boolean(entry.pendingUrl));
  }, []);

  const handleHistoryNavigate = useCallback((index) => {
    if (index < 0 || index >= historyEntries.length || index === historyIndex) return;
    setHistoryIndex(index);
    restoreHistoryEntry(historyEntries[index]);
  }, [historyEntries, historyIndex, restoreHistoryEntry]);

  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return;
    const nextIndex = historyIndex - 1;
    setHistoryIndex(nextIndex);
    restoreHistoryEntry(historyEntries[nextIndex]);
  }, [historyEntries, historyIndex, restoreHistoryEntry]);

  const handleRedo = useCallback(() => {
    if (historyIndex >= historyEntries.length - 1) return;
    const nextIndex = historyIndex + 1;
    setHistoryIndex(nextIndex);
    restoreHistoryEntry(historyEntries[nextIndex]);
  }, [historyEntries, historyIndex, restoreHistoryEntry]);

  const handleSubmit = useCallback(async (event, submittedPrompt, submittedImages = uploadedImages) => {
    event?.preventDefault?.();
    if (isGenerating || isSaving || !onGeneratePreview) return;

    const rawPrompt = typeof submittedPrompt === 'string' ? submittedPrompt : prompt;
    const hasCanvasWork =
      editorRef.current?.hasCanvasContent?.() ||
      textLayers.length > 0 ||
      imageLayers.length > 0 ||
      hasAdjustments(colorAdjustments);

    const finalPrompt = rawPrompt?.trim() || (hasCanvasWork ? AUTO_EDIT_PROMPT : '');
    if (!finalPrompt && submittedImages.length === 0) return;

    setIsGenerating(true);
    setError(null);
    setContentPolicyError(null);

    try {
      const compositeFile = await editorRef.current?.renderCompositeCanvas?.();
      const files = [];
      if (compositeFile) files.push(compositeFile);
      submittedImages.forEach((image) => {
        if (image.file && !image.isAssetMention) files.push(image.file);
      });

      const referenceImageUrls = submittedImages
        .filter((image) => image.isAssetMention && image.imageUrl)
        .map((image) => image.imageUrl);

      const result = await onGeneratePreview({
        prompt: finalPrompt,
        files,
        referenceImageUrls,
        model: imageModel,
      });

      const previewUrl = result?.imageUrl || result?.asset?.imageUrl || result?.url;
      if (!previewUrl) throw new Error('No preview image returned');

      editorRef.current?.setPendingPreview?.(previewUrl, finalPrompt, textLayers, imageLayers);
      setHasPendingPreview(true);
      pushHistory('Generated preview', {
        textLayers,
        imageLayers,
        colorAdjustments,
        pendingUrl: previewUrl,
        pendingPrompt: finalPrompt,
      });
      resetTransientInputs();
    } catch (err) {
      if (err?.code === 'CONTENT_POLICY_VIOLATION') {
        setContentPolicyError({ originalPrompt: err.originalPrompt || finalPrompt });
        setError(null);
      } else {
        setError('Failed to generate image preview.');
        setContentPolicyError(null);
      }
    } finally {
      setIsGenerating(false);
    }
  }, [
    colorAdjustments,
    imageLayers,
    imageModel,
    isGenerating,
    isSaving,
    onGeneratePreview,
    prompt,
    pushHistory,
    resetTransientInputs,
    textLayers,
    uploadedImages,
  ]);

  const handleRetry = useCallback((editedPrompt) => {
    setPrompt(editedPrompt);
    setContentPolicyError(null);
    return handleSubmit(null, editedPrompt, uploadedImages);
  }, [handleSubmit, uploadedImages]);

  const handleSave = useCallback(async () => {
    if (isGenerating || isSaving) return;
    setIsSaving(true);
    setError(null);

    try {
      const pendingPreview = editorRef.current?.getPendingPreview?.();
      let updated = null;

      if (pendingPreview?.url) {
        if (!onCommitPreview) throw new Error('Preview commit is not available');
        updated = await onCommitPreview({
          prompt: pendingPreview.prompt || prompt || '',
          pendingUrl: pendingPreview.url,
          model: imageModel,
        });
        const nextUrl = updated?.imageUrl || updated?.asset?.imageUrl || pendingPreview.url;
        editorRef.current?.updateCurrentUrl?.(nextUrl, false);
        editorRef.current?.setPendingPreviewFromHistory?.(null, '');
        editorRef.current?.clearDiscardRestoreState?.();
        setHasPendingPreview(false);
      } else {
        if (!onCommitUpload) throw new Error('Rendered image save is not available');
        const compositeFile = await editorRef.current?.renderCompositeCanvas?.();
        if (!compositeFile) throw new Error('No image edits to save');
        updated = await onCommitUpload({
          prompt: prompt || '',
          file: compositeFile,
          model: imageModel,
        });
        const nextUrl = updated?.imageUrl || updated?.asset?.imageUrl;
        if (nextUrl) editorRef.current?.updateCurrentUrl?.(nextUrl, false);
      }

      setTextLayers([]);
      setImageLayers([]);
      setColorAdjustments(DEFAULT_ADJUSTMENTS);
      editorRef.current?.clearCanvas?.();
      setHasPendingPreview(false);
      resetTransientInputs();
      onCommitted(updated);
      onSaveComplete(updated);
    } catch (err) {
      setError('Failed to save image edits.');
    } finally {
      setIsSaving(false);
    }
  }, [
    imageModel,
    isGenerating,
    isSaving,
    onCommitPreview,
    onCommitUpload,
    onCommitted,
    onSaveComplete,
    prompt,
    resetTransientInputs,
  ]);

  const handleDiscardPreview = useCallback(() => {
    editorRef.current?.discardPendingPreview?.();
    setHasPendingPreview(false);
    pushHistory('Discard preview', { pendingUrl: null, pendingPrompt: '' });
  }, [pushHistory]);

  const handleCameraAngleApply = useCallback(async () => {
    if (!onCameraAnglePreview || isGenerating || isSaving || isCameraAngleGenerating) return;
    const { horizontal, vertical, zoom } = cameraAngle;
    const hasChanges = horizontal !== 0 || vertical !== 0 || zoom !== 5;
    if (!hasChanges) return;

    setIsCameraAngleGenerating(true);
    setError(null);
    try {
      const result = await onCameraAnglePreview({
        horizontalAngle: horizontal,
        verticalAngle: vertical,
        zoom,
        model: imageModel,
      });
      const previewUrl = result?.imageUrl || result?.asset?.imageUrl || result?.url;
      if (!previewUrl) throw new Error('No camera angle preview returned');
      const anglePrompt = `Camera angle ${horizontal} deg, tilt ${vertical} deg, zoom ${zoom}`;
      editorRef.current?.setPendingPreview?.(previewUrl, anglePrompt, textLayers, imageLayers);
      setHasPendingPreview(true);
      pushHistory('Camera angle preview', {
        pendingUrl: previewUrl,
        pendingPrompt: anglePrompt,
        textLayers,
        imageLayers,
        colorAdjustments,
      });
      setCameraAngle({ horizontal: 0, vertical: 0, zoom: 5 });
    } catch (err) {
      setError('Failed to generate camera angle preview.');
    } finally {
      setIsCameraAngleGenerating(false);
    }
  }, [
    cameraAngle,
    colorAdjustments,
    imageLayers,
    imageModel,
    isCameraAngleGenerating,
    isGenerating,
    isSaving,
    onCameraAnglePreview,
    pushHistory,
    textLayers,
  ]);

  const handleRestoreVersion = useCallback(async (versionNumber) => {
    if (!onRestoreVersion) return;
    const updated = await onRestoreVersion(versionNumber);
    if (updated) {
      const nextUrl = updated?.imageUrl || updated?.asset?.imageUrl;
      if (nextUrl) editorRef.current?.updateCurrentUrl?.(nextUrl, false);
      onCommitted(updated);
    }
    return updated;
  }, [onCommitted, onRestoreVersion]);

  const handleExportImage = useCallback(async () => {
    setIsExporting(true);
    try {
      const file = await editorRef.current?.renderCompositeCanvas?.();
      if (!file) return;
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(imageName || 'asset').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase()}-edit.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  }, [imageName]);

  const isGlobalAssetWorkspace = assetMentionScope === 'global';
  const projectMentionableItems = useMemo(() => {
    const assetItems = allAssets
      .filter((asset) => getAssetMediaType(asset) === 'image' && asset?.imageUrl && asset.id !== imageId)
      .map((asset) => ({
        id: asset.id,
        orgAssetId: isGlobalAssetWorkspace ? asset.id : undefined,
        name: asset.name,
        displayName: asset.name,
        mediaType: 'image',
        imageUrl: asset.imageUrl,
        type: isGlobalAssetWorkspace ? 'org-asset' : 'asset',
        mentionSection: isGlobalAssetWorkspace ? 'Global Assets' : 'Project Assets',
        variations: isGlobalAssetWorkspace
          ? (asset.subAssets || [])
            .filter((subAsset) => (
              getAssetMediaType(subAsset) === 'image' &&
              subAsset?.imageUrl &&
              subAsset.id !== imageId
            ))
            .map((subAsset) => ({
              id: subAsset.id,
              orgAssetId: subAsset.id,
              parentAssetId: asset.id,
              name: subAsset.name || 'Variation',
              displayName: subAsset.name || 'Variation',
              mediaType: 'image',
              imageUrl: subAsset.imageUrl,
              type: 'org-asset',
              mentionSection: 'Global Assets',
            }))
          : [],
      }));

    const keyframeItems = (Array.isArray(projectKeyframes) ? projectKeyframes : [])
      .filter((kf) => kf?.id && kf?.keyframeUrl && kf.id !== imageId)
      .map((kf) => ({
        id: `keyframe-${kf.id}`,
        name: kf.name || kf.prompt || `Keyframe ${kf.id.slice(0, 8)}…`,
        displayName: kf.name || 'Keyframe',
        imageUrl: kf.keyframeUrl,
        type: 'keyframe',
      }));

    return [...assetItems, ...keyframeItems];
  }, [allAssets, imageId, isGlobalAssetWorkspace, projectKeyframes]);
  const { mentionableItems, handleMentionSelect, handleMentionHover } = useAssetMentionSupport({
    projectId,
    projectAssets: isGlobalAssetWorkspace ? [] : allAssets.filter((asset) => asset?.id !== imageId),
    items: projectMentionableItems,
    includeGlobalAssets: !isGlobalAssetWorkspace,
    excludeAssetIds: [imageId],
    acceptsMediaTypes: IMAGE_REFERENCE_MEDIA_TYPES,
  });

  const handleEnhancePrompt = useCallback(async () => {
    if (!prompt.trim() || isEnhancingPrompt || isGenerating || isSaving || isCameraAngleGenerating) return;
    try {
      setIsEnhancingPrompt(true);
      const response = await enhanceImagePrompt({ text: prompt });
      if (response?.enhanced_prompt) {
        setPrompt(response.enhanced_prompt);
      }
    } catch (err) {
      console.error('[ImageEditingWorkspace] Failed to enhance prompt:', err);
    } finally {
      setIsEnhancingPrompt(false);
    }
  }, [isCameraAngleGenerating, isEnhancingPrompt, isGenerating, isSaving, prompt]);

  const clearMagicSelectState = useCallback(() => {
    setMagicSelectState(createEmptyMagicSelectState());
  }, []);

  const handleMagicSelectLasso = useCallback(async (box, centerX, centerY) => {
    if (!onMagicSelect) return;
    setMagicSelectState((prev) => ({
      ...prev,
      isSearching: true,
      clickPosition: { x: centerX, y: centerY },
      normalizedBox: box,
      selectedAction: null,
    }));

    try {
      const displayedUrl =
        editorRef.current?.getDisplayedUrl?.() ||
        editorRef.current?.getCurrentUrl?.() ||
        imageUrl;
      const response = await onMagicSelect({ box, imageUrl: displayedUrl });
      if (!response?.success) throw new Error('Segmentation failed');

      const newSelection = {
        maskedImageUrl: response.maskedImageUrl,
        maskOverlayUrl: response.maskOverlayUrl,
        bounds: response.bounds,
      };
      const { mergeSelectionsIntoUnion } = await import('../../lib/maskUtils');
      const mergeResult = await mergeSelectionsIntoUnion(
        magicSelectSelectionsRef.current || [],
        newSelection,
        response.imageWidth,
        response.imageHeight,
      );

      setMagicSelectState((prev) => ({
        ...prev,
        isSearching: false,
        selections: mergeResult.selections,
        mergedMaskOverlayUrl: mergeResult.mergedMaskOverlayUrl,
        mergedMaskedImageUrl: mergeResult.mergedMaskedImageUrl,
        maskedImageUrl: mergeResult.mergedMaskedImageUrl,
        maskOverlayUrl: mergeResult.mergedMaskOverlayUrl,
        bounds: mergeResult.mergedBounds,
        imageWidth: response.imageWidth,
        imageHeight: response.imageHeight,
      }));
    } catch (err) {
      setError('Failed to select image region.');
      setMagicSelectState((prev) => ({ ...prev, isSearching: false }));
    }
  }, [imageUrl, onMagicSelect]);

  const handleMagicSelectAction = useCallback((action) => {
    setActiveTool('cursor');
    setMagicSelectState((prev) => ({ ...prev, selectedAction: action }));
  }, []);

  const handleMagicSelectEditPromptChange = useCallback((nextPrompt) => {
    setMagicSelectState((prev) => ({ ...prev, editPrompt: nextPrompt }));
  }, []);

  const buildSelectionLayer = useCallback(() => {
    if (!magicSelectState.mergedMaskedImageUrl || !magicSelectState.bounds) return null;
    const imgWidth = magicSelectState.imageWidth || 1920;
    const imgHeight = magicSelectState.imageHeight || 1080;
    const bounds = magicSelectState.bounds;
    return {
      id: `img-${Date.now()}`,
      imageUrl: magicSelectState.mergedMaskedImageUrl,
      position: {
        x: (imgWidth - bounds.width) / 2,
        y: (imgHeight - bounds.height) / 2,
      },
      width: bounds.width,
      height: bounds.height,
      cropX: bounds.x,
      cropY: bounds.y,
      cropWidth: bounds.width,
      cropHeight: bounds.height,
      originalImageWidth: imgWidth,
      originalImageHeight: imgHeight,
      scale: 1,
      rotation: 0,
      name: 'Selection',
    };
  }, [magicSelectState.bounds, magicSelectState.imageHeight, magicSelectState.imageWidth, magicSelectState.mergedMaskedImageUrl]);

  const handleMagicSelectDuplicate = useCallback(() => {
    const newLayer = buildSelectionLayer();
    if (!newLayer) return;
    setActiveTool('cursor');
    setImageLayers((prev) => {
      const next = [...prev, newLayer];
      pushHistory('Duplicated selection', { imageLayers: next });
      return next;
    });
    clearMagicSelectState();
  }, [buildSelectionLayer, clearMagicSelectState, pushHistory]);

  const runSelectionEdit = useCallback(async (effectivePrompt, historyLabel, extraImageLayers = null) => {
    if (!effectivePrompt?.trim() || !magicSelectState.bounds || !onGeneratePreview) return null;
    setActiveTool('cursor');
    setIsGenerating(true);
    setError(null);
    try {
      const compositeFile = await editorRef.current?.renderCompositeCanvas?.();
      let sourceUrl = null;
      if (compositeFile) {
        sourceUrl = URL.createObjectURL(compositeFile);
      } else {
        sourceUrl =
          editorRef.current?.getDisplayedUrl?.() ||
          editorRef.current?.getCurrentUrl?.() ||
          imageUrl;
      }
      const guideFile = await createSelectionGuideFile(sourceUrl, magicSelectState.bounds);
      if (compositeFile && sourceUrl?.startsWith('blob:')) URL.revokeObjectURL(sourceUrl);

      const result = await onGeneratePreview({
        prompt: effectivePrompt,
        files: [guideFile],
        referenceImageUrls: [],
        model: imageModel,
      });
      const previewUrl = result?.imageUrl || result?.asset?.imageUrl || result?.url;
      if (!previewUrl) throw new Error('No selection edit preview returned');

      editorRef.current?.updateCurrentUrl?.(previewUrl, false);
      setHasPendingPreview(false);
      pushHistory(historyLabel, {
        currentUrl: previewUrl,
        textLayers,
        imageLayers: extraImageLayers || imageLayers,
        colorAdjustments,
        pendingUrl: null,
        pendingPrompt: effectivePrompt,
      });
      clearMagicSelectState();
      resetTransientInputs();
      return result;
    } catch (err) {
      setError('Failed to edit selected region.');
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [
    clearMagicSelectState,
    colorAdjustments,
    imageLayers,
    imageModel,
    imageUrl,
    magicSelectState.bounds,
    onGeneratePreview,
    pushHistory,
    resetTransientInputs,
    textLayers,
  ]);

  const handleMagicSelectCut = useCallback(async () => {
    const newLayer = buildSelectionLayer();
    if (!newLayer) return;
    const nextLayers = [...imageLayers, newLayer];
    setImageLayers(nextLayers);
    await runSelectionEdit('Remove the highlighted object from the scene', 'Cut selection', nextLayers);
  }, [buildSelectionLayer, imageLayers, runSelectionEdit]);

  const handleMagicSelectDelete = useCallback(async () => {
    await runSelectionEdit('Remove the highlighted object from the scene', 'Deleted selection');
  }, [runSelectionEdit]);

  const handleMagicSelectEditSubmit = useCallback(async (promptOverride) => {
    const rawPrompt = (typeof promptOverride === 'string' ? promptOverride : magicSelectState.editPrompt || '').trim();
    if (!rawPrompt) return;
    const finalPrompt = rawPrompt.toLowerCase().startsWith(SELECTION_EDIT_PREFIX.toLowerCase())
      ? rawPrompt
      : `${SELECTION_EDIT_PREFIX}${rawPrompt}`;
    const label = rawPrompt.length > 30 ? `${rawPrompt.slice(0, 27)}...` : rawPrompt;
    await runSelectionEdit(finalPrompt, label || 'Edited selection');
  }, [magicSelectState.editPrompt, runSelectionEdit]);

  const handleAssetSelect = useCallback((asset) => {
    if (!asset?.imageUrl) return;
    const baseDims = editorRef.current?.getBaseImageDimensions?.();
    const baseImageWidth = baseDims?.width || 1920;
    const baseImageHeight = baseDims?.height || 1080;
    const img = new Image();
    img.onload = () => {
      const naturalWidth = img.naturalWidth || 512;
      const naturalHeight = img.naturalHeight || 512;
      const minSide = Math.min(baseImageWidth, baseImageHeight);
      const maxSize = Math.max(180, Math.min(420, Math.round(minSide * 0.35)));
      let width;
      let height;
      if (naturalWidth >= naturalHeight) {
        width = Math.min(naturalWidth, maxSize);
        height = Math.round((naturalHeight / naturalWidth) * width);
      } else {
        height = Math.min(naturalHeight, maxSize);
        width = Math.round((naturalWidth / naturalHeight) * height);
      }
      const newLayer = {
        id: `image-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        imageUrl: asset.imageUrl,
        position: {
          x: Math.max(0, Math.round((baseImageWidth - width) / 2)),
          y: Math.max(0, Math.round((baseImageHeight - height) / 2)),
        },
        width,
        height,
        scale: 1,
        rotation: 0,
        originalImageWidth: baseImageWidth,
        originalImageHeight: baseImageHeight,
        name: asset.name || 'Sticker',
      };
      setImageLayers((prev) => {
        const next = [...prev, newLayer];
        pushHistory('Added sticker', { imageLayers: next });
        return next;
      });
      setActiveTool('cursor');
    };
    img.src = asset.imageUrl;
  }, [pushHistory]);

  const canvasNode = (
    <ImageEditingCanvas
      ref={editorRef}
      keyframeId={imageId}
      keyframeUrl={displayedImageUrl}
      allAssets={allAssets}
      initialPendingUrl={initialPendingUrl}
      onClearInitialPendingUrl={onClearInitialPendingUrl}
      activeTool={activeTool}
      onToolChange={setActiveTool}
      penColor={penColor}
      onPenColorChange={setPenColor}
      textLayers={textLayers}
      onTextLayersChange={setTextLayers}
      imageLayers={imageLayers}
      onImageLayersChange={setImageLayers}
      prompt={prompt}
      onPromptChange={setPrompt}
      isGenerating={isGenerating || isSaving || isCameraAngleGenerating}
      mentionableItems={mentionableItems}
      autoEditPrompt={AUTO_EDIT_PROMPT}
      autoEditPromptWithCutout={AUTO_EDIT_PROMPT}
      onAnnotationComplete={() => pushHistory('Drawing')}
      onLayerTransformComplete={() => pushHistory('Layer transform')}
      onTextLayerAdded={(layers) => {
        setTextLayers(layers);
        pushHistory('Text layer', { textLayers: layers });
      }}
      onTextLayerDeleted={(layers) => {
        setTextLayers(layers);
        pushHistory('Deleted text layer', { textLayers: layers });
      }}
      onImageLayerDeleted={(layers) => {
        setImageLayers(layers);
        pushHistory('Deleted image layer', { imageLayers: layers });
      }}
      magicSelectState={magicSelectState}
      onMagicSelectLasso={handleMagicSelectLasso}
      onMagicSelectAction={handleMagicSelectAction}
      onMagicSelectEditPromptChange={handleMagicSelectEditPromptChange}
      onMagicSelectDuplicate={handleMagicSelectDuplicate}
      onMagicSelectCut={handleMagicSelectCut}
      onMagicSelectDelete={handleMagicSelectDelete}
      onMagicSelectEditSubmit={handleMagicSelectEditSubmit}
      onMagicSelectClear={() => {
        setActiveTool('cursor');
        clearMagicSelectState();
      }}
      colorAdjustments={colorAdjustments}
      isVersionPreview={isVersionPreview}
      onSave={handleSave}
      onComplete={onBack || (() => {})}
    />
  );

  const promptNode = (
    <ImageEditPromptBox
      value={prompt}
      onChange={setPrompt}
      onSubmit={handleSubmit}
      isLoading={isGenerating || isCameraAngleGenerating || isSaving || isEnhancingPrompt}
      isEnhancing={isEnhancingPrompt}
      placeholder={promptPlaceholder}
      mentionableItems={mentionableItems}
      onMentionSelect={handleMentionSelect}
      onMentionHover={handleMentionHover}
      uploadedImages={uploadedImages}
      setUploadedImages={setUploadedImages}
      error={error}
      contentPolicyError={contentPolicyError}
      onContentPolicyRetry={handleRetry}
      onContentPolicyDismiss={() => setContentPolicyError(null)}
      resizable={variant === 'fullscreen'}
      resizeMinHeight={36}
      resizeDefaultHeight={44}
      resizeMaxHeight={220}
      imageModel={imageModel}
      onImageModelChange={setImageModel}
      onEnhance={handleEnhancePrompt}
      enhanceButtonDisabled={
        isGenerating ||
        isSaving ||
        isCameraAngleGenerating ||
        isEnhancingPrompt ||
        !prompt.trim()
      }
      modelSelectorDisabled={isGenerating || isSaving || isCameraAngleGenerating || isEnhancingPrompt}
    />
  );

  if (variant === 'fullscreen') {
    return (
      <div className={`image-editing-workspace image-editing-workspace--fullscreen${sidePanel ? ' image-editing-workspace--with-side-panel' : ''}`}>
        <KeyframeEditingSidebar
          title={title}
          backLabel="Back"
          keyframeUrl={imageUrl}
          onBack={onBack || (() => {})}
          activeTool={activeTool}
          onToolChange={setActiveTool}
          enabledToolIds={['cursor', 'magic-select', 'pen', 'text', 'sticker', 'adjustments', 'camera']}
          onSave={handleSave}
          isSaving={isSaving}
          onExportImage={handleExportImage}
          isExporting={isExporting}
          exportTitle="Export current asset as PNG"
          saveTitle="Save current state as asset"
          penColor={penColor}
          onPenColorChange={setPenColor}
          assets={allAssets.filter((asset) => asset?.imageUrl && asset.id !== imageId)}
          onAssetSelect={handleAssetSelect}
          onStartCreatingAsset={onStartCreatingAsset}
          onStartUploadingAsset={onStartUploadingAsset}
          historyEntries={historyEntries}
          historyIndex={historyIndex}
          onHistoryNavigate={handleHistoryNavigate}
          onUndo={handleUndo}
          onRedo={handleRedo}
          colorAdjustments={colorAdjustments}
          onColorAdjustmentsChange={(next) => {
            setColorAdjustments(next);
            pushHistory('Color adjustment', { colorAdjustments: next });
          }}
          cameraAngle={cameraAngle}
          onCameraAngleChange={setCameraAngle}
          onCameraAngleApply={handleCameraAngleApply}
          isCameraAngleGenerating={isCameraAngleGenerating}
          enableWorldExplorer={false}
          keyframeVersions={versions}
          activeVersionNumber={activeVersionNumber}
          onRestoreVersion={handleRestoreVersion}
          isRestoringVersion={isRestoringVersion}
        />
        <main className="image-editing-fullscreen-main">
          <div className="image-editing-fullscreen-canvas">
            {canvasNode}
          </div>
          <div className="image-editing-fullscreen-prompt">
            {promptNode}
          </div>
        </main>
        {sidePanel && <aside className="image-editing-fullscreen-side">{sidePanel}</aside>}
      </div>
    );
  }

  return (
    <div className="image-editing-workspace">
      <div className="image-editing-toolbar" aria-label="Image editing tools">
        <div className="image-editing-tool-group">
          {TOOL_OPTIONS.map((tool) => {
            const Icon = tool.icon;
            const active = activeTool === tool.id;
            return (
              <button
                key={tool.id}
                type="button"
                className={`image-editing-tool-button${active ? ' active' : ''}`}
                onClick={() => setActiveTool(tool.id)}
                aria-pressed={active}
                title={tool.label}
              >
                <Icon size={16} />
              </button>
            );
          })}
        </div>

        {activeTool === 'pen' && (
          <input
            type="color"
            className="image-editing-color"
            value={penColor}
            onChange={(event) => setPenColor(event.target.value)}
            title="Pen color"
          />
        )}

        <div className="image-editing-save-actions">
          {onBack && (
            <button type="button" className="image-editing-secondary-button" onClick={onBack}>
              Done
            </button>
          )}
          {hasPendingPreview && (
            <button type="button" className="image-editing-secondary-button" onClick={handleDiscardPreview}>
              <RotateCcw size={14} />
              Discard Preview
            </button>
          )}
          <button type="button" className="image-editing-primary-button" onClick={handleSave} disabled={isSaving || isGenerating}>
            {isSaving ? <span>Saving...</span> : <><Save size={14} /><span>Save</span></>}
          </button>
        </div>
      </div>

      {activeTool === 'adjustments' && (
        <div className="image-editing-adjustments">
          <AdjustmentControl label="Brightness" value={colorAdjustments.brightness} onChange={(value) => setColorAdjustments((prev) => ({ ...prev, brightness: value }))} />
          <AdjustmentControl label="Contrast" value={colorAdjustments.contrast} onChange={(value) => setColorAdjustments((prev) => ({ ...prev, contrast: value }))} />
          <AdjustmentControl label="Saturation" value={colorAdjustments.saturation} onChange={(value) => setColorAdjustments((prev) => ({ ...prev, saturation: value }))} />
          <AdjustmentControl label="Temperature" value={colorAdjustments.temperature} onChange={(value) => setColorAdjustments((prev) => ({ ...prev, temperature: value }))} />
          <AdjustmentControl label="Tint" value={colorAdjustments.tint} onChange={(value) => setColorAdjustments((prev) => ({ ...prev, tint: value }))} />
          <button type="button" className="image-editing-secondary-button" onClick={() => setColorAdjustments(DEFAULT_ADJUSTMENTS)}>
            Reset
          </button>
        </div>
      )}

      <div className="image-editing-canvas-shell">
        <ImageEditingCanvas
          ref={editorRef}
          keyframeId={imageId}
          keyframeUrl={imageUrl}
          allAssets={allAssets}
          initialPendingUrl={initialPendingUrl}
          onClearInitialPendingUrl={onClearInitialPendingUrl}
          activeTool={activeTool}
          onToolChange={setActiveTool}
          penColor={penColor}
          onPenColorChange={setPenColor}
          textLayers={textLayers}
          onTextLayersChange={setTextLayers}
          imageLayers={imageLayers}
          onImageLayersChange={setImageLayers}
          prompt={prompt}
          onPromptChange={setPrompt}
          isGenerating={isGenerating || isSaving}
          mentionableItems={mentionableItems}
          autoEditPrompt={AUTO_EDIT_PROMPT}
          autoEditPromptWithCutout={AUTO_EDIT_PROMPT}
          onAnnotationComplete={() => {}}
          onLayerTransformComplete={() => {}}
          onTextLayerAdded={setTextLayers}
          onTextLayerDeleted={setTextLayers}
          onImageLayerDeleted={setImageLayers}
          colorAdjustments={colorAdjustments}
          onSave={handleSave}
          onComplete={onBack || (() => {})}
        />
      </div>

      <div className="image-editing-prompt">
        <ImageEditPromptBox
          value={prompt}
          onChange={setPrompt}
          onSubmit={handleSubmit}
          isLoading={isGenerating || isSaving || isEnhancingPrompt}
          isEnhancing={isEnhancingPrompt}
          placeholder={promptPlaceholder}
          mentionableItems={mentionableItems}
          onMentionSelect={handleMentionSelect}
          onMentionHover={handleMentionHover}
          uploadedImages={uploadedImages}
          setUploadedImages={setUploadedImages}
          error={error}
          contentPolicyError={contentPolicyError}
          onContentPolicyRetry={handleRetry}
          onContentPolicyDismiss={() => setContentPolicyError(null)}
          constrained={false}
          imageModel={imageModel}
          onImageModelChange={setImageModel}
          onEnhance={handleEnhancePrompt}
          enhanceButtonDisabled={isGenerating || isSaving || isEnhancingPrompt || !prompt.trim()}
          modelSelectorDisabled={isGenerating || isSaving || isEnhancingPrompt}
        />
      </div>
    </div>
  );
}

export default ImageEditingWorkspace;
